import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle, Clock, ChevronDown } from 'lucide-react';

export const StatusBadge = ({ status }) => {
  const config = {
    verification_pending: { label: 'Pending', icon: Clock, bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
    approved: { label: 'Approved', icon: CheckCircle, bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
    rejected: { label: 'Rejected', icon: XCircle, bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  };
  const c = config[status] || config.verification_pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${c.bg} ${c.text} border ${c.border}`} data-testid={`status-badge-${status}`}>
      <c.icon size={10} />
      {c.label}
    </span>
  );
};

export const StatusActions = ({ entry, onStatusChange, rejectionCodes = [] }) => {
  const [open, setOpen] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [rejectionCode, setRejectionCode] = useState('');
  const [showRemarks, setShowRemarks] = useState(false);

  const handleApprove = () => {
    onStatusChange(entry.id, 'approved', null);
    setOpen(false);
  };

  const handleReject = () => {
    if (!showRemarks) { setShowRemarks(true); return; }
    const fullRemarks = rejectionCode
      ? `${rejectionCode}: ${rejectionCodes.find(r => r.code === rejectionCode)?.description || ''}${remarks ? ` — ${remarks}` : ''}`
      : remarks || null;
    onStatusChange(entry.id, 'rejected', fullRemarks);
    setOpen(false);
    setShowRemarks(false);
    setRemarks('');
    setRejectionCode('');
  };

  const handleReset = () => {
    onStatusChange(entry.id, 'verification_pending', null);
    setOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => { setOpen(!open); setShowRemarks(false); setRemarks(''); setRejectionCode(''); }}
        className="p-1.5 text-[#9B9589] hover:text-[#0F1B2D] transition-colors border border-transparent hover:border-[#D8D3CB]"
        data-testid={`actions-${entry.id}`}
      >
        <ChevronDown size={14} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            className="absolute right-0 top-8 z-50 w-72 bg-white border-2 border-[#0F1B2D] shadow-lg"
          >
            <div className="p-1">
              {entry.status !== 'approved' && (
                <button onClick={handleApprove} className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 transition-colors" data-testid={`approve-${entry.id}`}>
                  <CheckCircle size={14} />Approve (Bypass Verification)
                </button>
              )}
              {entry.status !== 'rejected' && (
                <>
                  <button onClick={handleReject} className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-red-700 hover:bg-red-50 transition-colors" data-testid={`reject-${entry.id}`}>
                    <XCircle size={14} />{showRemarks ? 'Confirm Rejection' : 'Reject'}
                  </button>
                  {showRemarks && (
                    <div className="px-3 pb-2 space-y-2">
                      <select value={rejectionCode} onChange={(e) => setRejectionCode(e.target.value)} className="input-ainaa w-full px-2 py-1.5 text-xs" data-testid={`rejection-code-${entry.id}`}>
                        <option value="">Select rejection code...</option>
                        {rejectionCodes.map((rc) => (
                          <option key={rc.code} value={rc.code}>{rc.code}: {rc.description}</option>
                        ))}
                      </select>
                      <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} className="input-ainaa w-full px-2 py-1.5 text-xs min-h-[50px] resize-none" placeholder="Additional remarks (optional)" data-testid={`remarks-${entry.id}`} />
                    </div>
                  )}
                </>
              )}
              {entry.status !== 'verification_pending' && (
                <button onClick={handleReset} className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-[#9B9589] hover:bg-[#EDECEA] transition-colors" data-testid={`reset-${entry.id}`}>
                  <Clock size={14} />Reset to Pending
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
